import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Login.css'; // Reuse login styles

interface ResetForm {
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ResetForm>({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [resetComplete, setResetComplete] = useState(false);

  // Extract token and email from URL
  useEffect(() => {
    const urlToken = searchParams.get('token');
    const urlEmail = searchParams.get('email');
    
    if (!urlToken || !urlEmail) {
      setError('Invalid reset link. Please request a new password reset.');
      setVerifying(false);
      return;
    }

    setToken(urlToken);
    setEmail(urlEmail);

    // Verify token validity
    const verifyToken = async () => {
      try {
        setVerifying(true);
        const response = await apiService.verifyResetToken(urlEmail, urlToken);
        
        if (response.valid) {
          setTokenValid(true);
        } else {
          setError(response.message || 'Invalid or expired reset token');
        }
      } catch (error: any) {
        console.error('Token verification error:', error);
        setError('Invalid or expired reset token');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.resetPassword(email, token, formData.newPassword);
      setResetComplete(true);
    } catch (error: any) {
      console.error('Reset password error:', error);
      if (error.response?.status === 400) {
        setError('Invalid or expired reset token. Please request a new password reset.');
      } else {
        setError(error.response?.data?.detail || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Verifying Reset Link</h1>
            <p>Please wait while we verify your reset token...</p>
          </div>
          <div className="loading-spinner">
            <span className="spinner"></span>
            <p>Verifying...</p>
          </div>
        </div>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Password Reset Complete</h1>
            <p>Your password has been successfully updated</p>
          </div>

          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Success!</h3>
            <p>Your password has been reset successfully.</p>
            <p>You can now login with your new password.</p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="login-button"
          >
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Invalid Reset Link</h1>
            <p>This password reset link is invalid or has expired</p>
          </div>

          <div className="error-message">
            <span className="error-icon"><strong>⚠</strong></span>
            {error}
          </div>

          <div className="form-footer">
            <Link to="/forgot-password" className="login-button">
              Request New Reset Link
            </Link>
            
            <div className="login-footer">
              <Link to="/login" className="link-button">
                ← Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Set New Password</h1>
          <p>Create a strong password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-container">
              <input
                type={showPasswords ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter your new password"
                required
                autoComplete="new-password"
                className={error ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPasswords(!showPasswords)}
                aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
              >
                {showPasswords ? '👁' : '●'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your new password"
              required
              autoComplete="new-password"
              className={error ? 'error' : ''}
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon"><strong>⚠</strong></span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !formData.newPassword || !formData.confirmPassword}
            className="login-button"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          <div className="login-footer">
            <Link to="/login" className="link-button">
              ← Back to Login
            </Link>
          </div>
        </form>

        {/* Password Requirements */}
        <div className="security-info">
          <h3>🔒 Password Requirements</h3>
          <ul>
            <li>At least 8 characters long</li>
            <li>Contains at least one uppercase letter</li>
            <li>Contains at least one lowercase letter</li>
            <li>Contains at least one number</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;